import { ProtectedRoute } from '@shared/auth';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HiDownload } from 'react-icons/hi';
import { HiXMark } from 'react-icons/hi2';
import { useSearchParams } from 'react-router-dom';

import { useGetCoverageTagLookupsForMultipleDrugs } from '@/api/coverage-tags';
import { useGetPaTypeLookups } from '@/api/pa-types';
import { useGetBoardMeetingTypes } from '@/api/use-board-meeting-types';
import { useDrugsInTherapeuticClass } from '@/api/use-drugs-in-therapeutic-class';
import { useGetPoolsLookups } from '@/api/use-pools';
import { useGetStateLookups } from '@/api/use-states';
import { type InfiniteTherapeuticClassReportParams,useInfiniteTherapeuticClassReport } from '@/api/use-therapeutic-class-report-infinite';
import { type TherapeuticClassReportSummaryParams, useTherapeuticClassReportSummary } from '@/api/use-therapeutic-class-report-summary';
import { useTrackedClassifications } from '@/api/use-tracked-classifications';
import { Button } from '@/components-new/button';
import { Field } from '@/components-new/field';
import { MultiListbox, MultiListboxOption } from '@/components-new/multi-listbox';
import { SearchableListbox, SearchableListboxOption } from '@/components-new/searchable-listbox';
import { SectionDescription, SectionHeading } from '@/components-new/section';
import { TreeMultiSelect } from '@/components-new/tree-multi-select';
import { displayPdlStatus,PdlStatus } from '@/features/coverage/types/pdl-status';
import { WindowedTherapeuticClassTable } from '@/features/therapeutic-class/components/windowed-therapeutic-class-table';
import { useTherapeuticClassExport } from '@/features/therapeutic-class/hooks/use-therapeutic-class-export';
import { useCurrentUser } from '@/hooks/use-current-user';

const TherapeuticClassReportPage = () => {
  const { hasPolicies } = useCurrentUser();

  // Check if user is internal (Artia staff) or external (client user)
  const isInternalUser = hasPolicies(['isAnyArtiaUser']);

  // State for all dropdowns
  const [selectedTherapeuticClass, setSelectedTherapeuticClass] = useState<any>(null);
  const [selectedDrugs, setSelectedDrugs] = useState<any[]>([]);
  const [selectedStates, setSelectedStates] = useState<any[]>([]);
  const [selectedPools, setSelectedPools] = useState<any[]>([]);
  const [selectedDrugsAndTags, setSelectedDrugsAndTags] = useState<any[]>([]); // Combined drugs and coverage tags
  const [selectedPaTypes, setSelectedPaTypes] = useState<any[]>([]);
  const [selectedPdlStatuses, setSelectedPdlStatuses] = useState<any[]>([]);
  const [selectedBoardMeetingTypes, setSelectedBoardMeetingTypes] = useState<any[]>([]);

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'Ascending' | 'Descending'>('Ascending');

  // Fetch data for dropdowns
  const { data: states = [] } = useGetStateLookups();
  const { data: pools = [] } = useGetPoolsLookups();
  const { data: paTypes = [] } = useGetPaTypeLookups();
  const { data: boardMeetingTypes = [] } = useGetBoardMeetingTypes();
  const { data: drugsInClass = [], isLoading: isLoadingDrugs, error: drugsError } = useDrugsInTherapeuticClass(selectedTherapeuticClass?.id);

  // Fetch coverage tags for all selected drugs using the new bulk API
  const selectedDrugIdsForCoverageTags = useMemo(() => selectedDrugs.map(drug => drug.id), [selectedDrugs]);
  const { data: coverageTagsData = [] } = useGetCoverageTagLookupsForMultipleDrugs({
    drugIds: selectedDrugIdsForCoverageTags,
    queryConfig: {
      enabled: selectedDrugIdsForCoverageTags.length > 0
    }
  });

  // Fetch therapeutic classes from API (client-purchased classes)
  const { data: therapeuticClassOptions = [], isLoading: isLoadingClassifications } = useTrackedClassifications();

  const [searchParams] = useSearchParams();
  const preselectedClassificationId = searchParams.get('classificationId');
  const preselectionApplied = useRef(false);

  useEffect(() => {
    if (preselectionApplied.current || !preselectedClassificationId || therapeuticClassOptions.length === 0) return;
    const match = therapeuticClassOptions.find(c => c.id === parseInt(preselectedClassificationId, 10));
    if (match) {
      preselectionApplied.current = true;
      handleTherapeuticClassChange(match);
    }
  }, [preselectedClassificationId, therapeuticClassOptions]);

  // Track if initial selection has been done to avoid re-selecting
  const [hasInitialPoolSelection, setHasInitialPoolSelection] = useState(false);
  const [hasInitialDrugSelection, setHasInitialDrugSelection] = useState(false);

  // Auto-select all pools on initial load (only once) - this will provide coverage of all states
  // We don't auto-select individual states to avoid conflicts with pool selection
  useEffect(() => {
    if (pools.length > 0 && selectedPools.length === 0 && !hasInitialPoolSelection) {
      setSelectedPools(pools);
      setHasInitialPoolSelection(true);
    }
  }, [pools, selectedPools.length, hasInitialPoolSelection]);

  // Auto-select all drugs when therapeutic class changes
  useEffect(() => {
    if (drugsInClass.length > 0 && !hasInitialDrugSelection) {
      setSelectedDrugs(drugsInClass);
      // Also auto-select all drugs in the merged drugs & coverage tags filter
      const drugOptions = drugsInClass.map(drug => ({
        id: `drug-${drug.id}`,
        label: drug.label,
        type: 'drug',
        drugId: drug.id,
        drug: drug
      }));
      setSelectedDrugsAndTags(drugOptions);
      setHasInitialDrugSelection(true);
    }
  }, [drugsInClass, hasInitialDrugSelection]);

  const handleTherapeuticClassChange = (value: any) => {
    setSelectedTherapeuticClass(value);
    setSelectedDrugs([]); // Reset drugs when class changes
    setSelectedDrugsAndTags([]); // Also reset merged filter
    setHasInitialDrugSelection(false); // Reset flag so drugs can be auto-selected for new class
  };


  const handleStatesChange = (value: any[]) => {
    setSelectedStates(value);
  };

  const handlePoolsChange = (value: any[]) => {
    setSelectedPools(value);

    // When pools change, filter selected states to only include those in the selected pools
    if (value.length > 0) {
      const statesInSelectedPools = new Set<string>();
      value.forEach(pool => {
        pool.state?.forEach((state: any) => {
          statesInSelectedPools.add(state.code);
        });
      });

      // Filter current selected states to only keep those in the selected pools
      const filteredStates = selectedStates.filter(state => {
        return statesInSelectedPools.has(state.code);
      });

      if (filteredStates.length !== selectedStates.length) {
        setSelectedStates(filteredStates);
      }
    }
  };

  const handleCoverageTagsChange = (value: any[]) => {
    // Transform tree nodes back to the format expected by the rest of the code
    const transformedSelections = value.map(treeNode => ({
      id: treeNode.id,
      label: treeNode.label,
      type: treeNode.data.type,
      drugId: treeNode.data.drugId,
      drug: treeNode.data.drug,
      coverageTagId: treeNode.data.coverageTagId
    }));

    setSelectedDrugsAndTags(transformedSelections);
  };

  const handlePaTypesChange = (value: any[]) => {
    setSelectedPaTypes(value);
  };

  const handlePdlStatusesChange = (value: any[]) => {
    setSelectedPdlStatuses(value);
  };

  const handleBoardMeetingTypesChange = (value: any[]) => {
    setSelectedBoardMeetingTypes(value);
  };

  // Handle sorting changes
  const handleSortChange = (columnId: string, direction: 'Ascending' | 'Descending') => {
    setSortBy(columnId);
    setSortDirection(direction);
  };

  // Create PDL Status options from enum
  const pdlStatusOptions = useMemo(() => [
    { id: PdlStatus.BLANK, label: displayPdlStatus(PdlStatus.BLANK) },
    { id: PdlStatus.PREFERRED, label: displayPdlStatus(PdlStatus.PREFERRED) },
    { id: PdlStatus.NON_PREFERRED, label: displayPdlStatus(PdlStatus.NON_PREFERRED) },
    { id: PdlStatus.NON_PDL, label: displayPdlStatus(PdlStatus.NON_PDL) },
    { id: PdlStatus.NO_STATE_PDL, label: displayPdlStatus(PdlStatus.NO_STATE_PDL) },
    { id: PdlStatus.PREFERRED_NOT_LISTED, label: displayPdlStatus(PdlStatus.PREFERRED_NOT_LISTED) },
    { id: PdlStatus.NON_PREFERRED_NOT_LISTED, label: displayPdlStatus(PdlStatus.NON_PREFERRED_NOT_LISTED) },
    { id: PdlStatus.PENDING, label: displayPdlStatus(PdlStatus.PENDING) },
  ], []);

  // Extract coverage tag IDs from the combined selection
  const selectedCoverageTagIds = useMemo(() => {
    return selectedDrugsAndTags
      .filter(item => item.type === 'tag')
      .map(item => item.coverageTagId);
  }, [selectedDrugsAndTags]);

  // Extract drug IDs from the combined selection
  // Include drugs that are explicitly selected OR have coverage tags selected
  const selectedDrugIds = useMemo(() => {
    const drugIds = new Set<number>();

    selectedDrugsAndTags.forEach(item => {
      if (item.type === 'drug') {
        drugIds.add(item.drugId);
      } else if (item.type === 'tag') {
        // Auto-include drug when its coverage tag is selected
        drugIds.add(item.drugId);
      }
    });

    return Array.from(drugIds);
  }, [selectedDrugsAndTags]);

  // Extract PA Type IDs from selected PA types
  const selectedPaTypeIds = useMemo(() => {
    return selectedPaTypes.map(paType => paType.id);
  }, [selectedPaTypes]);

  // Extract PDL Status values from selected PDL statuses
  const selectedPdlStatusValues = useMemo(() => {
    return selectedPdlStatuses.map(status => status.id);
  }, [selectedPdlStatuses]);

  // Extract Board Meeting Type IDs from selected board meeting types
  const selectedBoardMeetingTypeIds = useMemo(() => {
    return selectedBoardMeetingTypes.map(type => type.id);
  }, [selectedBoardMeetingTypes]);

  // Calculate available states based on selected pools
  const availableStates = useMemo(() => {
    if (selectedPools.length === 0) {
      // If no pools selected, all states are available
      return states;
    }

    // If pools are selected, only show states that are in those pools
    const statesInSelectedPools = new Set<string>();
    selectedPools.forEach(pool => {
      pool.state?.forEach((state: any) => {
        statesInSelectedPools.add(state.code);
      });
    });

    return states.filter(state => statesInSelectedPools.has(state.code));
  }, [states, selectedPools]);

  // Prepare API parameters for the therapeutic class report with infinite scrolling
  const reportParams: InfiniteTherapeuticClassReportParams = useMemo(() => {
    const params = {
      classificationId: selectedTherapeuticClass?.id,
      drugIds: selectedDrugIds,
      stateIds: selectedStates.map(state => state.id),
      poolIds: selectedPools.map(pool => pool.id),
      coverageTagIds: selectedCoverageTagIds,
      paTypeIds: selectedPaTypeIds,
      pdlStatuses: selectedPdlStatusValues,
      boardMeetingTypeIds: selectedBoardMeetingTypeIds,
      pageSize: 250, // Updated to match standard page size for better performance
      sortBy: sortBy || undefined,
      sortDirection: sortBy ? sortDirection : undefined
    };

    return params;
  }, [selectedTherapeuticClass, selectedDrugIds, selectedStates, selectedPools, selectedCoverageTagIds, selectedPaTypeIds, selectedPdlStatusValues, selectedBoardMeetingTypeIds, sortBy, sortDirection]);

  // Prepare summary parameters (same as report params but without pageSize)
  const summaryParams: TherapeuticClassReportSummaryParams = useMemo(() => {
    return {
      classificationId: selectedTherapeuticClass?.id,
      drugIds: selectedDrugIds,
      stateIds: selectedStates.map(state => state.id),
      poolIds: selectedPools.map(pool => pool.id),
      coverageTagIds: selectedCoverageTagIds,
      paTypeIds: selectedPaTypeIds,
      pdlStatuses: selectedPdlStatusValues,
      boardMeetingTypeIds: selectedBoardMeetingTypeIds,
    };
  }, [selectedTherapeuticClass, selectedDrugIds, selectedStates, selectedPools, selectedCoverageTagIds, selectedPaTypeIds, selectedPdlStatusValues, selectedBoardMeetingTypeIds]);

  // Fetch summary data separately
  const {
    data: summaryData,
    isLoading: isLoadingSummary
  } = useTherapeuticClassReportSummary(
    summaryParams,
    !!(selectedTherapeuticClass && selectedDrugIds.length > 0 && (selectedStates.length > 0 || selectedPools.length > 0))
  );

  // Fetch therapeutic class report data with infinite scrolling
  const {
    data: reportData,
    isLoading: isLoadingReport,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: reportError
  } = useInfiniteTherapeuticClassReport(
    reportParams,
    !!(selectedTherapeuticClass && selectedDrugIds.length > 0 && (selectedStates.length > 0 || selectedPools.length > 0))
  );

  const summaryMetrics = useMemo(() => {
    if (summaryData) {
      return {
        totalDrugs: summaryData.numberOfDrugsAnalyzed || 0,
        totalStates: summaryData.numberOfStatesAnalyzed || 0,
        mostPreferred: summaryData.mostPreferredDrug || '-',
        mostCommonPA: summaryData.mostCommonPaType || 'N/A'
      };
    }

    // Fallback to selected counts if no data yet
    return {
      totalDrugs: selectedDrugIds.length,
      totalStates: 0,
      mostPreferred: '-',
      mostCommonPA: 'N/A'
    };
  }, [summaryData, selectedDrugIds.length]);

  // Prepare export parameters for backend Excel export
  const exportParams = useMemo(() => ({
    classificationId: selectedTherapeuticClass?.id,
    drugIds: selectedDrugIds,
    stateIds: selectedStates.map(state => state.id),
    poolIds: selectedPools.map(pool => pool.id),
    coverageTagIds: selectedCoverageTagIds,
    paTypeIds: selectedPaTypeIds,
    pdlStatuses: selectedPdlStatusValues,
    boardMeetingTypeIds: selectedBoardMeetingTypeIds,
  }), [selectedTherapeuticClass, selectedDrugIds, selectedStates, selectedPools, selectedCoverageTagIds, selectedPaTypeIds, selectedPdlStatusValues, selectedBoardMeetingTypeIds]);

  // Export functionality - server-side only
  const { handleExport, canExport, isExporting } = useTherapeuticClassExport({
    exportParams: exportParams,
  });

  // Clear all filters function
  const handleClearAllFilters = () => {
    setSelectedTherapeuticClass(null);
    setSelectedDrugs([]);
    setSelectedStates([]);
    setSelectedPools([]);
    setSelectedDrugsAndTags([]);
    setSelectedPaTypes([]);
    setSelectedPdlStatuses([]);
    setSelectedBoardMeetingTypes([]);
    setSortBy('');
    setSortDirection('Ascending');
    // Reset auto-selection flags to allow re-selection when new data is available
    setHasInitialPoolSelection(false);
    setHasInitialDrugSelection(false);
  };

  // Count valid (non-disabled) selections for drugs and tags
  const validDrugsAndTagsCount = selectedDrugsAndTags.filter(item => !item.disabled).length;

  // Check if any filters are applied
  const hasFiltersApplied = selectedTherapeuticClass || validDrugsAndTagsCount > 0 || selectedStates.length > 0 || selectedPools.length > 0 || selectedPaTypes.length > 0 || selectedPdlStatuses.length > 0 || selectedBoardMeetingTypes.length > 0;

  // Count of applied filters for better UX
  const appliedFiltersCount = [
    selectedTherapeuticClass ? 1 : 0,
    validDrugsAndTagsCount > 0 ? 1 : 0,
    selectedStates.length > 0 ? 1 : 0,
    selectedPools.length > 0 ? 1 : 0,
    selectedPaTypes.length > 0 ? 1 : 0,
    selectedPdlStatuses.length > 0 ? 1 : 0,
    selectedBoardMeetingTypes.length > 0 ? 1 : 0
  ].reduce((sum, count) => sum + count, 0);

  // Transform combined options into tree structure for TreeMultiSelect
  const treeOptions = useMemo(() => {
    const treeNodes: any[] = [];

    selectedDrugs.forEach((drug) => {
      // Add parent drug node
      treeNodes.push({
        id: `drug-${drug.id}`,
        label: drug.label,
        type: 'parent',
        data: {
          type: 'drug',
          drugId: drug.id,
          drug: drug
        }
      });

      // Add child coverage tag nodes
      if (coverageTagsData && coverageTagsData.length > 0) {
        const drugCoverageTags = coverageTagsData.filter((tag: any) => tag.drugId === drug.id);

        if (drugCoverageTags.length > 0) {
          drugCoverageTags.forEach((tag: any) => {
            // Use just the coverage tag name, not the combined drug + tag name
            const tagName = tag.coverageTagName || tag.name || tag.label || 'Unknown Coverage Tag';
            treeNodes.push({
              id: `tag-${tag.coverageTagId || tag.id || Math.random()}-${drug.id}`,
              label: tagName,
              type: 'child',
              parentId: `drug-${drug.id}`,
              data: {
                type: 'tag',
                drugId: drug.id,
                drug: drug,
                coverageTagId: tag.coverageTagId || tag.id
              }
            });
          });
        }
      }
    });

    return treeNodes;
  }, [selectedDrugs, coverageTagsData]);

  // Convert current selections back to tree node format for TreeMultiSelect
  const selectedTreeNodes = useMemo(() => {
    return selectedDrugsAndTags.map(item => {
      const treeNode = treeOptions.find(node => node.id === item.id);
      return treeNode || {
        id: item.id,
        label: item.label,
        type: item.type === 'drug' ? 'parent' : 'child',
        parentId: item.type === 'tag' ? `drug-${item.drugId}` : undefined,
        data: {
          type: item.type,
          drugId: item.drugId,
          drug: item.drug,
          coverageTagId: item.coverageTagId
        }
      };
    });
  }, [selectedDrugsAndTags, treeOptions]);

  return (
    <div className="mt-4 min-h-[calc(100vh-25px-54px)]">
      <div className="flex sm:flex-auto">
        <div className="basis-3/4">
          <SectionHeading level={2}>Therapeutic Class Report</SectionHeading>
          <SectionDescription>
            Compare drugs side-by-side within a therapeutic class by state and PDL positioning
          </SectionDescription>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <SectionHeading level={3}>Selection</SectionHeading>
          {hasFiltersApplied && (
            <Button
              outline
              onClick={handleClearAllFilters}
              className="text-sm"
              title={`Clear all ${appliedFiltersCount} applied filter${appliedFiltersCount > 1 ? 's' : ''}`}
            >
              <HiXMark/>
              Reset all selections
            </Button>
          )}
        </div>
        <div className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
          <Field
            label="Therapeutic Class"
            description={selectedTherapeuticClass ? `Selected: ${selectedTherapeuticClass.label}` : "No therapeutic class selected"}
          >
            {isLoadingClassifications ? (
              <div className="rounded-lg border border-zinc-200 py-4 text-center text-gray-500">
                Loading therapeutic classes...
              </div>
            ) : therapeuticClassOptions.length === 0 ? (
              <div className="rounded-lg border border-zinc-200 py-4 text-center text-gray-500">
                No therapeutic classes available for your client
              </div>
            ) : (
              <SearchableListbox
                value={selectedTherapeuticClass}
                onChange={handleTherapeuticClassChange}
                placeholder="Select therapeutic class..."
                searchPlaceholder="Search therapeutic classes..."
                getDisplayValue={(item: any) => item ? item.label : ''}
                getSearchValue={(item: any) => item ? item.label : ''}
                disabled={isLoadingClassifications}
              >
                {therapeuticClassOptions.map((classification) => (
                  <SearchableListboxOption key={classification.id} value={classification}>
                    {classification.label}
                  </SearchableListboxOption>
                ))}
              </SearchableListbox>
            )}
          </Field>

          <Field
            label="Drugs & Coverage Tags"
            description={selectedDrugs.length === 0
              ? "Select drugs to see available coverage tags"
              : `${selectedDrugIds.length} drugs selected, ${selectedCoverageTagIds.length} coverage tags selected`
            }
          >
            {!selectedTherapeuticClass ? (
              <MultiListbox
                value={[]}
                onChange={() => {}}
                placeholder="Select a therapeutic class first"
                searchPlaceholder="Search drugs..."
                getDisplayValue={(item: any) => item.label}
                getSearchValue={(item: any) => item.label}
                disabled={true}
              >
              </MultiListbox>
            ) : isLoadingDrugs ? (
              <MultiListbox
                value={[]}
                onChange={() => {}}
                placeholder="Loading drugs..."
                searchPlaceholder="Search drugs..."
                getDisplayValue={(item: any) => item.label}
                getSearchValue={(item: any) => item.label}
                disabled={true}
              >
              </MultiListbox>
            ) : drugsError ? (
              <MultiListbox
                value={[]}
                onChange={() => {}}
                placeholder={`Error loading drugs: ${drugsError.message}`}
                searchPlaceholder="Search drugs..."
                getDisplayValue={(item: any) => item.label}
                getSearchValue={(item: any) => item.label}
                disabled={true}
              >
              </MultiListbox>
            ) : drugsInClass.length === 0 ? (
              <MultiListbox
                value={[]}
                onChange={() => {}}
                placeholder="No drugs found for this therapeutic class"
                searchPlaceholder="Search drugs..."
                getDisplayValue={(item: any) => item.label}
                getSearchValue={(item: any) => item.label}
                disabled={true}
              >
              </MultiListbox>
            ) : (
              <TreeMultiSelect
                value={selectedTreeNodes}
                onChange={handleCoverageTagsChange}
                options={treeOptions}
                placeholder="Select drugs and coverage tags..."
                searchPlaceholder="Search drugs and coverage tags..."
                getDisplayValue={(item) => item.label}
                getSearchValue={(item) => item.label}
              />
            )}
          </Field>

          <Field
            label="States"
            description={selectedPools.length > 0
              ? `${selectedStates.length} of ${availableStates.length} states selected (from ${selectedPools.length} selected pools)`
              : `${selectedStates.length} of ${availableStates.length} states selected`
            }
          >
            {availableStates.length === 0 && selectedPools.length > 0 ? (
              <div className="rounded-lg border border-zinc-200 py-4 text-center text-gray-500">
                No states available in the selected pools
              </div>
            ) : (
              <MultiListbox
                value={selectedStates}
                onChange={handleStatesChange}
                placeholder={selectedPools.length > 0 ? 'Select states from your pools...' : 'Select states...'}
                searchPlaceholder="Search states..."
                getDisplayValue={(state) => state.label}
                getSearchValue={(state) => state.label}
              >
                {availableStates.map((state) => (
                  <MultiListboxOption key={state.code} value={state}>
                    {state.label}
                  </MultiListboxOption>
                ))}
              </MultiListbox>
            )}
          </Field>

          <Field
            label="Pools"
            description={`${selectedPools.length} of ${pools.length} pools selected (selecting pools will filter available states)`}
          >
            <MultiListbox
              value={selectedPools}
              onChange={handlePoolsChange}
              placeholder="Select pools..."
              searchPlaceholder="Search pools..."
              getDisplayValue={(pool) => pool.label}
              getSearchValue={(pool) => pool.label}
            >
              {pools.map((pool) => (
                <MultiListboxOption key={pool.code} value={pool}>
                  {pool.label}
                </MultiListboxOption>
              ))}
            </MultiListbox>
          </Field>

          <Field
            label="PA Types"
            description={`${selectedPaTypes.length} of ${paTypes.length} PA types selected`}
          >
            <MultiListbox
              value={selectedPaTypes}
              onChange={handlePaTypesChange}
              placeholder="Select PA types..."
              searchPlaceholder="Search PA types..."
              getDisplayValue={(paType) => paType.label}
              getSearchValue={(paType) => paType.label}
            >
              {paTypes.map((paType) => (
                <MultiListboxOption key={paType.id} value={paType}>
                  {paType.label}
                </MultiListboxOption>
              ))}
            </MultiListbox>
          </Field>

          <Field
            label="PDL Statuses"
            description={`${selectedPdlStatuses.length} of ${pdlStatusOptions.length} PDL statuses selected`}
          >
            <MultiListbox
              value={selectedPdlStatuses}
              onChange={handlePdlStatusesChange}
              placeholder="Select PDL statuses..."
              searchPlaceholder="Search PDL statuses..."
              getDisplayValue={(status) => status.label}
              getSearchValue={(status) => status.label}
            >
              {pdlStatusOptions.map((status) => (
                <MultiListboxOption key={status.id} value={status}>
                  {status.label}
                </MultiListboxOption>
              ))}
            </MultiListbox>
          </Field>

          <Field
            label="Meeting Types"
            description={`${selectedBoardMeetingTypes.length} of ${boardMeetingTypes.length} board meeting types selected`}
          >
            <MultiListbox
              value={selectedBoardMeetingTypes}
              onChange={handleBoardMeetingTypesChange}
              placeholder="Select board meeting types..."
              searchPlaceholder="Search board meeting types..."
              getDisplayValue={(type) => type.label}
              getSearchValue={(type) => type.label}
            >
              {boardMeetingTypes.map((type) => (
                <MultiListboxOption key={type.id} value={type}>
                  {type.label}
                </MultiListboxOption>
              ))}
            </MultiListbox>
          </Field>
        </div>
      </div>

      {selectedTherapeuticClass && selectedDrugIds.length > 0 && (selectedStates.length > 0 || selectedPools.length > 0) && (
        <div className="mt-8">
          <SectionHeading level={3}>Summary</SectionHeading>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex size-8 items-center justify-center rounded-md bg-slate-500 text-white">
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Drugs Analyzed</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {isLoadingSummary ? '...' : summaryMetrics.totalDrugs}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex size-8 items-center justify-center rounded-md bg-emerald-600 text-white">
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">States Analyzed</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {isLoadingSummary ? '...' : summaryMetrics.totalStates}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex size-8 items-center justify-center rounded-md bg-indigo-600 text-white">
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Most Preferred Drug</div>
                  <div className="text-lg font-semibold text-gray-900 truncate" title={summaryMetrics.mostPreferred}>
                    {isLoadingSummary ? '...' : summaryMetrics.mostPreferred}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex size-8 items-center justify-center rounded-md bg-amber-600 text-white">
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Most Common PA Type</div>
                  <div className="text-lg font-semibold text-gray-900 truncate" title={summaryMetrics.mostCommonPA}>
                    {isLoadingSummary ? '...' : summaryMetrics.mostCommonPA}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <div>
            <SectionHeading level={3}>Therapeutic Class Comparison</SectionHeading>
          </div>
          <div className="flex items-center gap-2">
            <Button
              color="secondary"
              onClick={handleExport}
              disabled={!canExport || isLoadingReport || isExporting || !selectedTherapeuticClass}
            >
              <HiDownload/>
              {isExporting ? 'Exporting...' : isLoadingReport ? 'Loading...' : 'Export'}
            </Button>
          </div>
        </div>

        <div className="mt-6">
          {selectedTherapeuticClass && selectedDrugIds.length > 0 && (selectedStates.length > 0 || selectedPools.length > 0) ? (
            <WindowedTherapeuticClassTable
              data={reportData || []}
              showDrugColumn={true}
              showStepTherapyColumn={isInternalUser}
              isLoading={isLoadingReport}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
          ) : (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-12 text-center dark:border-zinc-700 dark:bg-zinc-900/50">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                Select Options to View Report
              </h3>
              <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                Choose a therapeutic class, at least one drug, and either states or pools to generate the comparison table
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
                {!selectedTherapeuticClass && (
                  <span className="rounded bg-red-100 px-2 py-1 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                    Therapeutic class required
                  </span>
                )}
                {selectedDrugIds.length === 0 && (
                  <span className="rounded bg-yellow-100 px-2 py-1 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                    Select drugs
                  </span>
                )}
                {selectedStates.length === 0 && selectedPools.length === 0 && (
                  <span className="rounded bg-blue-100 px-2 py-1 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                    Select states or pools
                  </span>
                )}
              </div>
            </div>
          )}

          {reportError && selectedTherapeuticClass && selectedDrugIds.length > 0 && (selectedStates.length > 0 || selectedPools.length > 0) && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <h4 className="text-sm font-medium text-red-800 dark:text-red-400">
                Error Loading Report Data
              </h4>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                There was an issue loading the therapeutic class report. Please try again or contact support if the issue persists.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TherapeuticClassReportPageContainer = () => {
  return (
    <ProtectedRoute>
      <TherapeuticClassReportPage/>
    </ProtectedRoute>
  );
};

export default TherapeuticClassReportPageContainer;
